import { canEditDraft } from "./transitions";
import { QuoteStatus } from "./types";

// 초안을 더 못 고치는 까닭 — 새 버전을 뜰 수 있는 발송본(sent) · 수주/이탈 마감 · 같은 번호의 더 새 버전이 있음.
export type LockReason = "sent" | "won" | "lost" | "not_latest";

// 판정 한 곳 — 더 새 버전(초안 포함)이 있으면 그쪽으로 보내고, 없으면 상태로 가른다. DRAFT 면 null.
export const lockReasonOf = (
  quote: { status: QuoteStatus; version: number },
  versions: readonly { version: number }[]
): LockReason | null => {
  if (canEditDraft(quote.status)) return null;
  if (versions.some((v) => v.version > quote.version)) return "not_latest";
  if (quote.status === "WON") return "won";
  if (quote.status === "LOST") return "lost";
  return "sent";
};

const LOCKED_NOTES: Record<LockReason, string> = {
  sent: "이 견적은 발송되어 고칠 수 없습니다 — 상세에서 새 버전을 만들어 이어가세요",
  won: "수주로 마감된 견적이라 고칠 수 없습니다",
  lost: "이탈로 마감된 견적이라 고칠 수 없습니다",
  not_latest: "새 버전이 이미 있습니다 — 최신 버전에서 이어가세요",
};

export const lockedNoteOf = (reason: LockReason): string => LOCKED_NOTES[reason];

export const LOCK_REASONS: readonly LockReason[] = ["sent", "won", "lost", "not_latest"];

const LOCK_RANK: Record<LockReason, number> = { sent: 0, won: 1, lost: 1, not_latest: 2 };

// 잠긴 뒤 다른 잠김 신호가 닿으면 더 뒤 단계(발송 < 수주·이탈 < 새 버전)로만 바꾼다 — lockReasonOf 와 같은 우선순위.
export const strongerLockOf = (current: LockReason | null, incoming: LockReason): LockReason =>
  current === null || LOCK_RANK[incoming] > LOCK_RANK[current] ? incoming : current;

const continuesInNewVersion = (reason: LockReason): boolean => reason === "sent" || reason === "not_latest";

// 편집 내용 복사 뒤 안내 — 새 버전으로 이어갈 수 있는 까닭일 때만 붙여넣을 곳을 말한다(기존 줄 위에 붙이면 값이 섞이므로 빈 공종·맨 끝 빈 줄).
export const lockedCopyNoteOf = (reason: LockReason): string =>
  continuesInNewVersion(reason)
    ? "복사했습니다 — 새 버전의 새 공종(또는 공종 맨 끝 빈 줄) 첫 칸에 붙여넣으세요"
    : "복사했습니다";

// 복사 TSV 가 옮기지 못하는 것 — 공종·SET 은 평탄화되고, 상세 열이라 간단 보기에 붙이면 열이 밀린다.
export const LOCKED_COPY_LOSS_NOTE = "공종 구분·세트 묶음은 옮겨지지 않습니다 — 상세 보기에서 붙여넣으세요";

// 새 버전은 기존 항목을 이미 복사해 둔다 — 붙인 줄과 옛 줄이 겹친다.
export const LOCKED_COPY_OVERLAP_NOTE = "새 버전에는 기존 항목이 이미 들어 있어 그 줄 위에 붙이면 값이 섞입니다 — 붙인 뒤 겹치는 옛 줄은 지우세요";

// 복사 TSV 에 안 실리는 머리 정보 — 저장본과 비교해 달라진 것만 알린다.
export interface CopyHead {
  area_pyeong: number | null;
  // 면적 원본(m²) — 없으면 평에서 계산한다.
  area_m2?: number | null;
  title: string;
  mgmt_fee_pct: string;
  rounding: number | null;
  conditions: string[];
  extra_notice: string;
}

// 저장(toDraftInput)과 같은 다듬기 — 앞뒤 공백·빈 조건 줄·"4." 같은 중간값 차이는 달라진 것으로 안 본다.
export const copyHeadOf = (head: CopyHead): CopyHead => ({
  area_pyeong: head.area_pyeong,
  title: head.title.trim(),
  mgmt_fee_pct: String(Number(head.mgmt_fee_pct.trim())),
  rounding: head.rounding,
  conditions: head.conditions.map((c) => c.trim()).filter((c) => c.length > 0),
  extra_notice: head.extra_notice.trim(),
});

export const lostHeadNoteOf = (current: CopyHead, saved: CopyHead): string | null => {
  const now = copyHeadOf(current);
  const was = copyHeadOf(saved);
  const lost = [
    now.area_pyeong === was.area_pyeong ? null : now.area_pyeong === null ? "평수(비움)" : `평수 ${now.area_pyeong}평`,
    now.title === was.title ? null : "제목",
    now.mgmt_fee_pct === was.mgmt_fee_pct ? null : "관리비율",
    now.rounding === was.rounding ? null : "단수 정리",
    now.conditions.join("\n") === was.conditions.join("\n") ? null : "견적 조건",
    now.extra_notice === was.extra_notice ? null : "별도 부담금 안내",
  ].filter((name): name is string => name !== null);
  return lost.length === 0 ? null : `${lost.join("·")} — 복사에 안 실립니다. 새 버전에 따로 옮겨 적으세요`;
};

export const lockedCopyDescriptionOf = (reason: LockReason, headNote: string | null): string =>
  [continuesInNewVersion(reason) ? LOCKED_COPY_OVERLAP_NOTE : null, LOCKED_COPY_LOSS_NOTE, headNote]
    .filter((line): line is string => line !== null)
    .join("\n");
